import type { BittsIcon } from '@crossbeam/bitts';

import { DateTime } from 'luxon';

import { MDM_PROPERTIES, MDM_TYPES } from '@/constants/mdm';
import type { AddToCrmContactType } from '@/types/copilotContacts';
import type {
  RecordContactType,
  RecordFieldType,
} from '@/types/individual_record';

export const EMPTY_FIELD_VALUE = '—';

export const IRP_TAB = {
  overview: 'overview',
  details: 'details',
  contacts: 'contacts',
  opportunities: 'opportunities',
  timeline: 'timeline',
} as const;

export type IRPTabType = (typeof IRP_TAB)[keyof typeof IRP_TAB];

export const IRP_TAB_ROUTES: Record<IRPTabType, string> = {
  [IRP_TAB.overview]: 'individual_record_overview',
  [IRP_TAB.details]: 'individual_record_details',
  [IRP_TAB.contacts]: 'individual_record_contacts',
  [IRP_TAB.opportunities]: 'individual_record_opportunities',
  [IRP_TAB.timeline]: 'activity_timeline',
};

export type IRPPartnerColumnType =
  | 'score'
  | 'owner'
  | 'contacts'
  | 'opportunities'
  | 'populations';

export const IRP_TAB_SCOPES: Partial<Record<IRPTabType, string>> = {
  [IRP_TAB.timeline]: 'read:activity-timeline',
};

export const IRP_TABS: { name: string; value: IRPTabType; icon: BittsIcon }[] =
  [
    { name: 'Overview', value: IRP_TAB.overview, icon: ['fak', 'company'] },
    {
      name: 'Account Details',
      value: IRP_TAB.details,
      icon: ['fak', 'opportunity'],
    },
    {
      name: 'Opportunities',
      value: IRP_TAB.opportunities,
      icon: ['fak', 'opportunity'],
    },
    { name: 'Contacts', value: IRP_TAB.contacts, icon: ['fak', 'contacts'] },
    { name: 'Activity', value: IRP_TAB.timeline, icon: ['fak', 'time'] },
  ];

export const IRP_DETAILS_TAB_LABELS = {
  account: 'Account Details',
  lead: 'Lead Details',
} as const;

export const IRP_DETAILS_HEADINGS = {
  account: 'Account information',
  lead: 'Lead information',
} as const;

export const HEADER_ARR_FIELD_NAMES = [
  'arr',
  'annual revenue',
  'annualrevenue',
];

export function formatFieldNumber(value: number) {
  return new Intl.NumberFormat('en-US').format(value);
}

export function hasFieldValue({ value }: RecordFieldType) {
  return value !== null && value !== undefined && value !== '';
}

export function formatFieldValue(field: RecordFieldType) {
  const { value, data_type: dataType } = field;
  if (!hasFieldValue(field)) return EMPTY_FIELD_VALUE;
  if (dataType === 'datetime' && typeof value === 'string') {
    return DateTime.fromISO(value, { setZone: true }).toFormat(
      'EEE, MMM d yyyy',
    );
  }
  return String(value);
}

export function fieldCell(
  fields: RecordFieldType[],
  mdmProperty: string,
  fallbackLabel: string,
) {
  const field = fields.find((item) => item.mdm_property === mdmProperty);
  return {
    label: field?.name || fallbackLabel,
    value: field ? formatFieldValue(field) : EMPTY_FIELD_VALUE,
  };
}

export function remainingFields(
  fields: RecordFieldType[],
  listedProperties: string[],
) {
  return fields.filter(
    (field) =>
      (!field.mdm_property || !listedProperties.includes(field.mdm_property)) &&
      hasFieldValue(field),
  );
}

export function ownerNameOf(fields: RecordFieldType[]) {
  const USER_PROPERTIES = MDM_PROPERTIES[MDM_TYPES.USER];
  function valueFor(mdmProperty: string) {
    const field = fields.find((item) => item.mdm_property === mdmProperty);
    return field?.value == null ? '' : String(field.value);
  }
  return (
    valueFor(USER_PROPERTIES.NAME) ||
    [valueFor(USER_PROPERTIES.FIRST_NAME), valueFor(USER_PROPERTIES.LAST_NAME)]
      .filter(Boolean)
      .join(' ')
  );
}

function isClosedOpportunity({ fields }: { fields: RecordFieldType[] }) {
  const isClosed = fields.find(
    (field) => field.mdm_property === MDM_PROPERTIES[MDM_TYPES.DEAL].IS_CLOSED,
  )?.value;
  return String(isClosed).toLowerCase() === 'true';
}

export function openOpportunityCount(
  opportunities: { fields: RecordFieldType[] }[],
) {
  return opportunities.filter(
    (opportunity) => !isClosedOpportunity(opportunity),
  ).length;
}

export function addToCrmContactFromRecordContact({
  fields,
  crm_id: crmId,
  missing_from_crm: isMissingFromCrm,
}: RecordContactType): AddToCrmContactType {
  const CONTACT_PROPERTIES = MDM_PROPERTIES[MDM_TYPES.CONTACT];
  function valueFor(mdmProperty: string) {
    const field = fields.find((item) => item.mdm_property === mdmProperty);
    return field && hasFieldValue(field) ? String(field.value) : '';
  }
  const [firstNameFromName = '', ...lastNameFromName] = valueFor(
    CONTACT_PROPERTIES.NAME,
  ).split(' ');
  return {
    firstName:
      valueFor(CONTACT_PROPERTIES.FIRST_NAME) || firstNameFromName || null,
    lastName:
      valueFor(CONTACT_PROPERTIES.LAST_NAME) || lastNameFromName.join(' '),
    email: valueFor(CONTACT_PROPERTIES.EMAIL),
    phone: valueFor(CONTACT_PROPERTIES.PHONE) || null,
    title: valueFor(CONTACT_PROPERTIES.TITLE) || null,
    isInOwnCrm: isMissingFromCrm === false || !!crmId,
    crmId: crmId ?? null,
  };
}
